import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, ArrowRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { blogPosts } from '../data/blogPosts';
import { SEO } from './SEO';
import { buildArticleSchema, buildBreadcrumb } from '../lib/seo-schemas';

// Friendly labels for service slugs used in `relatedServices`
const serviceLabels = {
  'college-admissions': 'College Admissions Support',
  'career-path-planning': 'Career Path Planning',
  'domestic-student-support': 'Domestic Student Support',
  'application-assistance': 'Application Assistance',
  'one-on-one-mentorship': 'One-on-One Mentorship',
  'scholarship-guidance': 'Scholarship & Financial Aid',
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <SEO title="Article Not Found" path={`/blog/${slug}`} noindex />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900">Article Not Found</h1>
          <Link to="/blog" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = buildArticleSchema({
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: post.image,
    datePublished: post.date,
    slug: post.slug,
  });

  const breadcrumb = buildBreadcrumb([
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: post.title, path: `/blog/${post.slug}` },
  ]);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  };

  const related = (post.relatedServices || []).filter((s) => serviceLabels[s]);

  return (
    <div className="min-h-screen pt-32 pb-16 bg-gray-50">
      <SEO
        title={post.title}
        description={post.metaDescription || post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image}
        schema={[articleSchema, breadcrumb]}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-8 transition-colors duration-200"
          data-testid="blog-post-back"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Blog
        </Link>

        <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="relative h-72 sm:h-96 bg-gray-200">
            <img
              src={post.image}
              alt={post.title}
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4">
              <span className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                {post.category}
              </span>
            </div>
          </div>

          <div className="p-8 sm:p-12">
            <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </time>
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {post.readTime}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            {post.intro && (
              <p className="text-lg text-gray-700 leading-relaxed mb-8">{post.intro}</p>
            )}

            {post.sections.map((s, i) => (
              <section key={i} className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{s.heading}</h2>
                <p
                  className="text-gray-700 leading-relaxed"
                  // Content is hard-coded inside our own data file, so dangerouslySetInnerHTML
                  // is safe and lets us include internal links + bold inline.
                  dangerouslySetInnerHTML={{ __html: s.html }}
                />
              </section>
            ))}

            {/* Related services */}
            {related.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Related services from Elevated Education
                </h2>
                <ul className="space-y-2" data-testid="blog-post-related-services">
                  {related.map((s) => (
                    <li key={s}>
                      <Link
                        to={`/services/${s}`}
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline font-medium"
                      >
                        {serviceLabels[s]}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Strong end-of-post CTA */}
            <div className="mt-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-3">
                Ready to take the next step?
              </h3>
              <p className="text-blue-50 mb-6 leading-relaxed">
                Book a free 1-on-1 consultation with an Elevated Education advisor.
                We'll review your goals, recommend the best Canadian programs for you,
                and map out your application step by step — at no cost.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-white text-blue-700 hover:bg-gray-100"
                data-testid="blog-post-cta"
              >
                <CalendarIcon className="mr-2 w-5 h-5" />
                Book Free Consultation
              </Button>
            </div>
          </div>
        </article>

        {/* Related articles */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blogPosts
              .filter((p) => p.slug !== slug)
              .slice(0, 2)
              .map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow"
                >
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded mb-3">
                    {p.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 hover:text-blue-600">
                    {p.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{p.excerpt}</p>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
